import { openDB, type IDBPDatabase } from 'idb';

export type PendingOp = {
  id: string;
  table: string;
  op: 'insert' | 'update' | 'delete';
  payload?: any;
  match?: Record<string, any>;
  createdAt: number;
  attempts: number;
  lastError?: string;
  status: 'pending' | 'syncing' | 'conflict' | 'failed';
  conflictRemote?: any;
  baseSnapshot?: any;
};

const DB_NAME = 'jf-offline';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase> | null = null;

export function getDB(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('cache')) {
          db.createObjectStore('cache'); // key: `${table}` -> array
        }
        if (!db.objectStoreNames.contains('queue')) {
          const s = db.createObjectStore('queue', { keyPath: 'id' });
          s.createIndex('byStatus', 'status');
          s.createIndex('byTable', 'table');
        }
        if (!db.objectStoreNames.contains('meta')) {
          db.createObjectStore('meta'); // key: arbitrary
        }
      },
    });
  }
  return dbPromise;
}

export async function cacheSet(table: string, rows: any[]) {
  const db = await getDB();
  await db.put('cache', rows, table);
}

export async function cacheGet<T = any>(table: string): Promise<T[] | null> {
  const db = await getDB();
  return (await db.get('cache', table)) ?? null;
}

export async function queueAdd(op: Omit<PendingOp, 'id' | 'createdAt' | 'attempts' | 'status'>): Promise<PendingOp> {
  const db = await getDB();
  const full: PendingOp = {
    id: `op_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    createdAt: Date.now(),
    attempts: 0,
    status: 'pending',
    ...op,
  };
  await db.put('queue', full);
  notifyQueueChange();
  return full;
}

export async function queueAll(): Promise<PendingOp[]> {
  const db = await getDB();
  const all = await db.getAll('queue');
  return all.sort((a: PendingOp, b: PendingOp) => a.createdAt - b.createdAt);
}

export async function queueGet(id: string): Promise<PendingOp | undefined> {
  const db = await getDB();
  return db.get('queue', id);
}

export async function queueUpdate(op: PendingOp) {
  const db = await getDB();
  await db.put('queue', op);
  notifyQueueChange();
}

export async function queueDelete(id: string) {
  const db = await getDB();
  await db.delete('queue', id);
  notifyQueueChange();
}

export async function queueCount(): Promise<{ pending: number; conflict: number; failed: number }> {
  const all = await queueAll();
  return {
    pending: all.filter(o => o.status === 'pending' || o.status === 'syncing').length,
    conflict: all.filter(o => o.status === 'conflict').length,
    failed: all.filter(o => o.status === 'failed').length,
  };
}

const listeners = new Set<() => void>();
export function onQueueChange(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyQueueChange() {
  listeners.forEach(fn => { try { fn(); } catch {} });
}